import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FieldValues, Resolver } from "react-hook-form";
import { MUNICIPIO_MESORREGIAO, PARAIBA_MESORREGIOES, PARAIBA_MUNICIPIOS } from "@/data/paraibaMunicipios";

/* -------------------------------------------------------------------------- */
/* Edital e janela de inscrição                                               */
/* -------------------------------------------------------------------------- */

export const EDITAL_NUMBER = "001/2026";
export const EDITAL_VERSION = "001/2026-v1";
export const APPLICATION_FEE_BRL = 500;
export const MAX_SELECTED_COMPETITIONS = 5;
export const MIN_WEEKS_BETWEEN_STAGES = 4;
export const CONTACT_EMAIL = "federacaopbf3@gmail.com";
export const SEASON_YEAR = 2027;

// Horário de Brasília (UTC-3). Podem ser sobrescritas por env para testes/ajustes.
const DEFAULT_OPENS_AT = "2026-09-29T00:00:00-03:00";
const DEFAULT_CLOSES_AT = "2026-10-29T23:59:59-03:00";

function parseInstant(value: string | undefined, fallback: string): number {
  const parsed = Date.parse(value ?? "");
  return Number.isFinite(parsed) ? parsed : Date.parse(fallback);
}

export const CHAMAMENTO_OPENS_AT = parseInstant(import.meta.env.VITE_CHAMAMENTO_OPENS_AT, DEFAULT_OPENS_AT);
export const CHAMAMENTO_CLOSES_AT = parseInstant(import.meta.env.VITE_CHAMAMENTO_CLOSES_AT, DEFAULT_CLOSES_AT);

export type ChamamentoWindow = "upcoming" | "open" | "closed";

export function getChamamentoWindow(
  now: number = Date.now(),
  opensAt: number = CHAMAMENTO_OPENS_AT,
  closesAt: number = CHAMAMENTO_CLOSES_AT,
): ChamamentoWindow {
  if (now < opensAt) return "upcoming";
  if (now > closesAt) return "closed";
  return "open";
}

/** Nos últimos dias da janela os avisos do site passam a destacar a urgência. */
export const CHAMAMENTO_LAST_DAYS = 7;

const DAY_MS_WINDOW = 86_400_000;

export interface ChamamentoStatus {
  window: ChamamentoWindow;
  /** Dias inteiros que ainda restam (arredonda para cima); 0 fora da janela aberta. */
  daysLeft: number;
  lastDays: boolean;
}

export function getChamamentoStatus(
  now: number = Date.now(),
  opensAt: number = CHAMAMENTO_OPENS_AT,
  closesAt: number = CHAMAMENTO_CLOSES_AT,
): ChamamentoStatus {
  const window = getChamamentoWindow(now, opensAt, closesAt);
  const daysLeft = window === "open" ? Math.max(1, Math.ceil((closesAt - now) / DAY_MS_WINDOW)) : 0;
  return { window, daysLeft, lastDays: window === "open" && daysLeft <= CHAMAMENTO_LAST_DAYS };
}

/** "29/10" no fuso de Brasília. */
export function formatShortDate(ms: number): string {
  return new Date(ms).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit" });
}

/** Frase de prazo e rótulo do botão conforme o estado; os avisos do site (Home, Eventos, menu) usam a mesma fonte. */
export function getChamamentoCopy(
  status: ChamamentoStatus = getChamamentoStatus(),
  opensAt: number = CHAMAMENTO_OPENS_AT,
  closesAt: number = CHAMAMENTO_CLOSES_AT,
): { deadline: string; cta: string } {
  const close = formatShortDate(closesAt);
  if (status.window === "upcoming") {
    return { deadline: `Inscrições abrem em ${formatShortDate(opensAt)} e vão até ${close}.`, cta: "Ver as regras" };
  }
  if (status.window === "open") {
    const deadline = !status.lastDays
      ? `Inscrições abertas até ${close}.`
      : status.daysLeft <= 1
        ? `Último dia: inscrições até ${close}.`
        : `Últimos dias: inscrições até ${close}.`;
    return { deadline, cta: "Candidatar competição" };
  }
  return { deadline: `Inscrições encerradas em ${close}.`, cta: "Ver o resultado" };
}

/* -------------------------------------------------------------------------- */
/* Documentos e máscaras                                                      */
/* -------------------------------------------------------------------------- */

const onlyDigits = (value: string) => value.replace(/\D/g, "");

export function isValidCpf(raw: string): boolean {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  for (const length of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < length; i++) sum += Number(cpf[i]) * (length + 1 - i);
    const digit = ((sum * 10) % 11) % 10;
    if (digit !== Number(cpf[length])) return false;
  }
  return true;
}

export function isValidCnpj(raw: string): boolean {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  for (const length of [12, 13]) {
    const weights = length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < length; i++) sum += Number(cnpj[i]) * weights[i];
    const rest = sum % 11;
    const digit = rest < 2 ? 0 : 11 - rest;
    if (digit !== Number(cnpj[length])) return false;
  }
  return true;
}

export function maskCpf(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

export function maskCnpj(value: string): string {
  const d = onlyDigits(value).slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

export function maskPhone(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/* -------------------------------------------------------------------------- */
/* Opções do formulário                                                       */
/* -------------------------------------------------------------------------- */

/** Região derivada da cidade pela mesorregião oficial do IBGE; não é perguntada ao organizador. */
export const REGIONS = PARAIBA_MESORREGIOES;

export function regionOf(city: (typeof PARAIBA_MUNICIPIOS)[number]) {
  return MUNICIPIO_MESORREGIAO[city];
}

export const YES_NO_OPTIONS = [
  { value: "sim", label: "Sim" },
  { value: "nao", label: "Não" },
] as const;

export const DURATION_OPTIONS = [
  { value: "1", label: "1 dia" },
  { value: "2", label: "2 dias" },
  { value: "3", label: "3 dias" },
] as const;

export const VENUE_STATUS_OPTIONS = [
  { value: "confirmado", label: "Sim, confirmado" },
  { value: "negociacao", label: "Em negociação" },
  { value: "nao", label: "Não" },
] as const;

export const FACILITY_OPTIONS = [
  { value: "aquecimento", label: "Área de aquecimento" },
  { value: "area_coberta", label: "Área coberta" },
  { value: "vestiarios", label: "Vestiários" },
  { value: "som_locucao", label: "Som e locução" },
  { value: "telao", label: "Telão" },
  { value: "estacionamento", label: "Estacionamento" },
  { value: "alimentacao", label: "Alimentação" },
  { value: "ambulancia", label: "Ambulância" },
] as const;

export const CATEGORY_OPTIONS = [
  { value: "rx_masculino", label: "RX masculino" },
  { value: "rx_feminino", label: "RX feminino" },
  { value: "master_masculino", label: "Master masculino (35 a 50+)" },
  { value: "master_feminino", label: "Master feminino (35 a 50+)" },
  { value: "intermediario_masculino", label: "Intermediário masculino" },
  { value: "intermediario_feminino", label: "Intermediário feminino" },
  { value: "scaled_masculino", label: "Scaled masculino" },
  { value: "scaled_feminino", label: "Scaled feminino" },
  { value: "teen", label: "Teen" },
  { value: "outras", label: "Outras" },
] as const;

export const FORMAT_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "dupla", label: "Duplas" },
  { value: "trio", label: "Trios" },
  { value: "quarteto", label: "Quartetos" },
  { value: "misto", label: "Misto" },
] as const;

export const JUDGES_TRAINING_OPTIONS = [
  { value: "curso_fpf3", label: "Curso FPF3" },
  { value: "curso_cbff", label: "Curso CBFF" },
  { value: "outro", label: "Outro" },
  { value: "nenhum", label: "Nenhum" },
] as const;

export const SPONSOR_COUNTERPART_OPTIONS = [
  { value: "logo_materiais", label: "Logo nos materiais de divulgação" },
  { value: "backdrop_podio", label: "Backdrop do pódio" },
  { value: "estande", label: "Espaço de estande" },
  { value: "locucao", label: "Menção da locução" },
  { value: "posts", label: "Posts nas redes sociais" },
] as const;

type Option = { value: string; label: string };

const optionValues = <T extends readonly Option[]>(options: T) =>
  options.map((o) => o.value) as [T[number]["value"], ...T[number]["value"][]];

export const OPTION_LABELS: Record<string, string> = Object.fromEntries(
  [
    ...YES_NO_OPTIONS,
    ...DURATION_OPTIONS,
    ...VENUE_STATUS_OPTIONS,
    ...FACILITY_OPTIONS,
    ...CATEGORY_OPTIONS,
    ...FORMAT_OPTIONS,
    ...JUDGES_TRAINING_OPTIONS,
    ...SPONSOR_COUNTERPART_OPTIONS,
  ].map((o) => [o.value, o.label]),
);

/* -------------------------------------------------------------------------- */
/* Perguntas (fonte única para formulário e painel)                           */
/* -------------------------------------------------------------------------- */

export const QUESTIONS = {
  org_name: "Nome da organização ou razão social",
  cnpj: "CNPJ",
  responsible_name: "Nome completo do responsável legal",
  responsible_cpf: "CPF do responsável",
  email: "E-mail",
  whatsapp: "WhatsApp",
  instagram: "Instagram da organização ou do evento",
  box_name: "Box vinculado à organização",
  box_affiliated: "O box é filiado à FPF3?",
  competition_name: "Nome da competição",
  editions_held: "Edições já realizadas (quantas e em quais anos)",
  date_first: "Data pretendida em 2027 (1ª opção)",
  date_second: "Data alternativa (2ª opção)",
  duration_days: "Duração",
  city: "Cidade",
  venue_address: "Local e endereço do evento",
  venue_status: "O local já está confirmado?",
  capacity: "Capacidade máxima de atletas",
  last_edition_athletes: "Número de atletas na última edição",
  facilities: "Estrutura disponível",
  entry_fee: "Valor previsto de inscrição por atleta (R$)",
  prize: "Premiação prevista",
  categories: "Categorias oferecidas",
  formats: "Formatos de competição",
  workouts_count: "Número previsto de provas (WODs)",
  used_wodarena: "Já usou a plataforma WodArena?",
  judges_count: "Número previsto de juízes",
  judges_training: "Capacitação dos juízes",
  medical_plan: "Plano de atendimento médico (ambulância, socorrista, seguro dos atletas)",
  sponsors: "Patrocinadores atuais ou previstos do evento, com a categoria de cada um",
  sponsor_counterparts: "Contrapartidas que pode oferecer aos patrocinadores da Liga",
  followers: "Seguidores do perfil do evento",
  media_links: "Links de fotos, vídeos ou resultados de edições anteriores",
} as const;

export const ACCEPTANCE_TEXT = {
  accept_edital: `Li e concordo com o Edital de Chamamento nº ${EDITAL_NUMBER}.`,
  accept_fee: `Estou ciente de que, se selecionada, a competição pagará taxa de chancela de R$ 500,00 no prazo do Termo de Chancela.`,
  accept_wodarena:
    "Estou ciente de que a competição deverá usar a plataforma WodArena para inscrições, resultados e leaderboard.",
  accept_sponsors:
    "Estou ciente de que a competição deverá respeitar a exclusividade e as contrapartidas dos patrocinadores e parceiros oficiais da Liga PB 2027 (Anexo II).",
  accept_data:
    "Declaro que as informações são verdadeiras e autorizo o uso dos meus dados para este chamamento, conforme a Política de Privacidade da FPF3.",
} as const;

export type AcceptanceKey = keyof typeof ACCEPTANCE_TEXT;
export const ACCEPTANCE_KEYS = Object.keys(ACCEPTANCE_TEXT) as AcceptanceKey[];

export type QuestionKey = keyof typeof QUESTIONS;

export const FORM_SECTIONS: { id: string; title: string; description: string; fields: QuestionKey[] }[] = [
  {
    id: "A",
    title: "Organizador",
    description: "Quem responde pela competição perante a FPF3.",
    fields: [
      "org_name",
      "cnpj",
      "responsible_name",
      "responsible_cpf",
      "email",
      "whatsapp",
      "instagram",
      "box_name",
      "box_affiliated",
    ],
  },
  {
    id: "B",
    title: "Competição",
    description: "Data, local e duração pretendidos para 2027.",
    fields: [
      "competition_name",
      "editions_held",
      "date_first",
      "date_second",
      "duration_days",
      "city",
      "venue_address",
      "venue_status",
    ],
  },
  {
    id: "C",
    title: "Estrutura e público",
    description: "Capacidade, estrutura e valores previstos.",
    fields: ["capacity", "last_edition_athletes", "facilities", "entry_fee", "prize"],
  },
  {
    id: "D",
    title: "Categorias e formato",
    description: "As categorias precisam ser compatíveis com o Ranking Paraíba.",
    fields: ["categories", "formats", "workouts_count", "used_wodarena"],
  },
  {
    id: "E",
    title: "Arbitragem e segurança",
    description: "Como a competição garante julgamento e atendimento.",
    fields: ["judges_count", "judges_training", "medical_plan"],
  },
  {
    id: "F",
    title: "Divulgação e patrocínio",
    description: "Alcance do evento e contrapartidas aos patrocinadores da Liga.",
    fields: ["sponsors", "sponsor_counterparts", "followers", "media_links"],
  },
];

/* -------------------------------------------------------------------------- */
/* Esquema de validação                                                       */
/* -------------------------------------------------------------------------- */

const required = (label: string, max = 200) =>
  z.string().trim().min(1, `Informe: ${label}`).max(max, `Máximo de ${max} caracteres`);

const optionalText = (max = 300) => z.string().trim().max(max, `Máximo de ${max} caracteres`);

const digitsField = (label: string, { optional = false, max = 6 }: { optional?: boolean; max?: number } = {}) =>
  z
    .string()
    .trim()
    .refine((v) => (optional && v === "") || /^\d+$/.test(v), `${label}: use apenas números`)
    .refine((v) => v === "" || v.length <= max, "Número muito grande");

const moneyField = z
  .string()
  .trim()
  .refine((v) => /^\d+([.,]\d{1,2})?$/.test(v), "Informe um valor em reais, ex.: 250 ou 250,50");

const isoDate2027 = (label: string) =>
  z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, `Informe: ${label}`)
    .refine((v) => !Number.isNaN(Date.parse(`${v}T12:00:00Z`)), "Data inválida")
    .refine((v) => v.startsWith(`${SEASON_YEAR}-`), `A data deve ser em ${SEASON_YEAR}`);

const DATE_RULE_MESSAGE = "A data alternativa deve ser diferente da 1ª opção";

const choice = <T extends readonly Option[]>(options: T, label: string) =>
  z.enum(optionValues(options), { errorMap: () => ({ message: `Selecione: ${label}` }) });

const multiChoice = <T extends readonly Option[]>(options: T, label: string) =>
  z.array(z.enum(optionValues(options))).min(1, `Selecione ao menos uma opção: ${label}`);

export const applicationSchema = z
  .object({
    // A. Organizador
    org_name: required(QUESTIONS.org_name),
    cnpj: z
      .string()
      .trim()
      .refine((v) => v === "" || isValidCnpj(v), "CNPJ inválido"),
    responsible_name: required(QUESTIONS.responsible_name),
    responsible_cpf: z.string().trim().refine(isValidCpf, "CPF inválido"),
    email: z.string().trim().toLowerCase().email("E-mail inválido").max(200),
    whatsapp: z
      .string()
      .trim()
      .refine((v) => [10, 11].includes(onlyDigits(v).length), "Informe DDD e número"),
    instagram: optionalText(120),
    box_name: required(QUESTIONS.box_name),
    box_affiliated: choice(YES_NO_OPTIONS, QUESTIONS.box_affiliated),
    // B. Competição
    competition_name: required(QUESTIONS.competition_name),
    editions_held: required(QUESTIONS.editions_held, 300),
    date_first: isoDate2027(QUESTIONS.date_first),
    date_second: isoDate2027(QUESTIONS.date_second),
    duration_days: choice(DURATION_OPTIONS, QUESTIONS.duration_days),
    city: z.enum(PARAIBA_MUNICIPIOS, { errorMap: () => ({ message: "Selecione uma cidade da Paraíba" }) }),
    venue_address: required(QUESTIONS.venue_address, 300),
    venue_status: choice(VENUE_STATUS_OPTIONS, QUESTIONS.venue_status),
    // C. Estrutura e público
    capacity: digitsField("Capacidade"),
    last_edition_athletes: digitsField("Atletas na última edição", { optional: true }),
    facilities: multiChoice(FACILITY_OPTIONS, QUESTIONS.facilities),
    entry_fee: moneyField,
    prize: optionalText(1000),
    // D. Categorias e formato
    categories: multiChoice(CATEGORY_OPTIONS, QUESTIONS.categories),
    formats: multiChoice(FORMAT_OPTIONS, QUESTIONS.formats),
    workouts_count: digitsField("Número de provas", { max: 2 }),
    used_wodarena: choice(YES_NO_OPTIONS, QUESTIONS.used_wodarena),
    // E. Arbitragem e segurança
    judges_count: digitsField("Número de juízes", { max: 3 }),
    judges_training: choice(JUDGES_TRAINING_OPTIONS, QUESTIONS.judges_training),
    medical_plan: required(QUESTIONS.medical_plan, 1000),
    // F. Divulgação e patrocínio
    sponsors: required(QUESTIONS.sponsors, 1000),
    sponsor_counterparts: multiChoice(SPONSOR_COUNTERPART_OPTIONS, QUESTIONS.sponsor_counterparts),
    followers: digitsField("Seguidores", { optional: true, max: 9 }),
    media_links: optionalText(1000),
    // G. Ciência e aceite
    accept_edital: z.literal(true, { errorMap: () => ({ message: "O aceite é obrigatório" }) }),
    accept_fee: z.literal(true, { errorMap: () => ({ message: "O aceite é obrigatório" }) }),
    accept_wodarena: z.literal(true, { errorMap: () => ({ message: "O aceite é obrigatório" }) }),
    accept_sponsors: z.literal(true, { errorMap: () => ({ message: "O aceite é obrigatório" }) }),
    accept_data: z.literal(true, { errorMap: () => ({ message: "O aceite é obrigatório" }) }),
  })
  .refine((v) => v.date_first !== v.date_second, {
    path: ["date_second"],
    message: DATE_RULE_MESSAGE,
  });

export type ApplicationFormValues = z.input<typeof applicationSchema>;
export type ApplicationValues = z.output<typeof applicationSchema>;

export const STEP_FIELDS: (keyof ApplicationFormValues)[][] = [
  ...FORM_SECTIONS.map((s) => s.fields as (keyof ApplicationFormValues)[]),
  ACCEPTANCE_KEYS as (keyof ApplicationFormValues)[],
];

/**
 * Regras que envolvem mais de um campo. O `.refine` do esquema só roda quando todos os campos do
 * formulário já são válidos, então na navegação por etapas (com as etapas seguintes ainda vazias)
 * ele nunca dispara. Esta função é chamada a cada validação para que o erro apareça na etapa certa.
 */
export function getCrossFieldErrors(
  values: Partial<Pick<ApplicationFormValues, "date_first" | "date_second">>,
): Partial<Record<"date_second", string>> {
  const { date_first, date_second } = values;
  if (date_first && date_second && date_first === date_second) return { date_second: DATE_RULE_MESSAGE };
  return {};
}

/** Resolver do formulário: validação do esquema + regras entre campos em toda validação (etapa, blur ou envio). */
export const applicationResolver: Resolver<ApplicationFormValues, unknown, ApplicationValues> = async (
  values,
  context,
  options,
) => {
  const base = (await zodResolver(applicationSchema)(values, context, options)) as {
    values: FieldValues;
    errors: Record<string, unknown>;
  };
  const cross = getCrossFieldErrors(values);
  const names = Object.keys(cross) as (keyof typeof cross)[];
  if (names.length === 0) return base as never;

  const errors = { ...base.errors };
  for (const name of names) {
    if (!errors[name]) errors[name] = { type: "validate", message: cross[name], ref: options.fields?.[name]?.ref };
  }
  return { values: {}, errors } as never;
};

/** Primeiro campo com erro na ordem do formulário e a etapa (0-based) em que ele aparece. */
export function findFirstInvalid(errorNames: Iterable<string>): { name: string; step: number } | null {
  const invalid = new Set(errorNames);
  for (let step = 0; step < STEP_FIELDS.length; step++) {
    const name = STEP_FIELDS[step].find((field) => invalid.has(field));
    if (name) return { name, step };
  }
  return null;
}

export const EMPTY_FORM: ApplicationFormValues = {
  org_name: "",
  cnpj: "",
  responsible_name: "",
  responsible_cpf: "",
  email: "",
  whatsapp: "",
  instagram: "",
  box_name: "",
  box_affiliated: undefined as never,
  competition_name: "",
  editions_held: "",
  date_first: "",
  date_second: "",
  duration_days: undefined as never,
  city: undefined as never,
  venue_address: "",
  venue_status: undefined as never,
  capacity: "",
  last_edition_athletes: "",
  facilities: [],
  entry_fee: "",
  prize: "",
  categories: [],
  formats: [],
  workouts_count: "",
  used_wodarena: undefined as never,
  judges_count: "",
  judges_training: undefined as never,
  medical_plan: "",
  sponsors: "",
  sponsor_counterparts: [],
  followers: "",
  media_links: "",
  accept_edital: false as never,
  accept_fee: false as never,
  accept_wodarena: false as never,
  accept_sponsors: false as never,
  accept_data: false as never,
};

/** Converte os valores validados no corpo enviado à Edge Function (números como números). */
export function buildSubmissionPayload(values: ApplicationValues) {
  const { accept_edital, accept_fee, accept_wodarena, accept_sponsors, accept_data, ...answers } = values;
  const toNumber = (v: string) => (v === "" ? null : Number(v.replace(",", ".")));
  return {
    answers: {
      ...answers,
      region: regionOf(answers.city),
      capacity: toNumber(answers.capacity),
      last_edition_athletes: toNumber(answers.last_edition_athletes),
      entry_fee: toNumber(answers.entry_fee),
      workouts_count: toNumber(answers.workouts_count),
      judges_count: toNumber(answers.judges_count),
      followers: toNumber(answers.followers),
      duration_days: Number(answers.duration_days),
    },
    acceptances: { accept_edital, accept_fee, accept_wodarena, accept_sponsors, accept_data },
    edital_version: EDITAL_VERSION,
  };
}

/* -------------------------------------------------------------------------- */
/* Status, critérios e nota                                                   */
/* -------------------------------------------------------------------------- */

export const APPLICATION_STATUSES = [
  "recebida",
  "em_analise",
  "selecionada",
  "nao_selecionada",
  "suplente",
  "termo_assinado",
  "taxa_paga",
  "etapa_confirmada",
] as const;

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  recebida: "Recebida",
  em_analise: "Em análise",
  selecionada: "Selecionada",
  nao_selecionada: "Não selecionada",
  suplente: "Suplente",
  termo_assinado: "Termo assinado",
  taxa_paga: "Taxa paga",
  etapa_confirmada: "Etapa confirmada",
};

export const STATUS_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  recebida: ["em_analise"],
  em_analise: ["selecionada", "suplente", "nao_selecionada"],
  selecionada: ["termo_assinado", "nao_selecionada"],
  suplente: ["selecionada", "nao_selecionada"],
  termo_assinado: ["taxa_paga", "nao_selecionada"],
  taxa_paga: ["etapa_confirmada"],
  nao_selecionada: [],
  etapa_confirmada: [],
};

export const SCORE_CRITERIA = [
  { key: "historico", label: "Histórico e estrutura", weight: 25 },
  { key: "data_regiao", label: "Data e região (equilíbrio do calendário)", weight: 20 },
  { key: "categorias", label: "Categorias e formato compatíveis", weight: 20 },
  { key: "arbitragem", label: "Plano de arbitragem e segurança", weight: 15 },
  { key: "divulgacao", label: "Divulgação e contrapartidas", weight: 10 },
  { key: "valor", label: "Valor de inscrição acessível", weight: 10 },
] as const;

export type CriterionKey = (typeof SCORE_CRITERIA)[number]["key"];
export type ApplicationScores = Partial<Record<CriterionKey, number>>;

/** Média ponderada das notas 0–10 pelos pesos do edital. Retorna null enquanto faltar algum critério. */
export function computeFinalScore(scores: ApplicationScores): number | null {
  let total = 0;
  for (const { key, weight } of SCORE_CRITERIA) {
    const value = scores[key];
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 10) return null;
    total += value * weight;
  }
  return Math.round(total) / 100;
}

const DAY_MS = 86_400_000;

/** Ids de candidaturas cuja 1ª data fica a menos de `minWeeks` semanas de outra candidatura. */
export function findDateConflicts(
  items: { id: string; date: string }[],
  minWeeks: number = MIN_WEEKS_BETWEEN_STAGES,
): Set<string> {
  const conflicts = new Set<string>();
  const minMs = minWeeks * 7 * DAY_MS;
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = Date.parse(`${items[i].date}T12:00:00Z`);
      const b = Date.parse(`${items[j].date}T12:00:00Z`);
      if (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < minMs) {
        conflicts.add(items[i].id);
        conflicts.add(items[j].id);
      }
    }
  }
  return conflicts;
}

export function formatDateBr(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}
